import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Key, Trash, ShieldCheck } from "@phosphor-icons/react";
import { WorkerCombobox } from "@/components/WorkerCombobox";

const ROLES = [
  { value: "admin", label: "Admin (full access)" },
  { value: "analyst", label: "Analyst (read-only)" },
  { value: "manager", label: "Manager (proposals & packets)" },
  { value: "contractor", label: "Contractor (mobile portal)" },
];

const emptyForm = { email: "", name: "", role: "analyst", password: "", worker_id: "" };

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [pwOpenFor, setPwOpenFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = async () => {
    const [u, w] = await Promise.all([api.get("/users"), api.get("/workers")]);
    setUsers(Array.isArray(u.data) ? u.data : []);
    setWorkers(Array.isArray(w.data) ? w.data : []);
  };
  useEffect(() => { load(); }, []);

  const workerName = useMemo(() => (id) => workers.find((w) => w.id === id)?.name || "—", [workers]);
  // Workers who don't already have a linked contractor user (excluding this user's current worker in edit mode)
  const availableWorkers = useMemo(() => {
    const taken = new Set(users.filter((u) => u.role === "contractor" && u.id !== editingId).map((u) => u.worker_id));
    return workers.filter((w) => !taken.has(w.id));
  }, [workers, users, editingId]);

  const openNew = () => { setEditingId(null); setForm(emptyForm); setErr(""); setOpen(true); };
  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({ email: u.email, name: u.name, role: u.role, password: "", worker_id: u.worker_id || "" });
    setErr(""); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      if (editingId) {
        const payload = { name: form.name, role: form.role, worker_id: form.role === "contractor" ? form.worker_id : null };
        await api.patch(`/users/${editingId}`, payload);
        toast.success("User updated");
      } else {
        const payload = { email: form.email.trim(), name: form.name, role: form.role, password: form.password };
        if (form.role === "contractor") payload.worker_id = form.worker_id;
        await api.post("/users", payload);
        toast.success("User created");
      }
      setOpen(false); load();
    } catch (e2) { setErr(e2.response?.data?.detail?.toString() || e2.message); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/users/${pwOpenFor.id}/reset-password`, { new_password: newPw });
      toast.success(`Password reset for ${pwOpenFor.email}`);
      setPwOpenFor(null); setNewPw("");
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
  };

  const doResetMfa = async (u) => {
    if (!window.confirm(`Reset two-factor authentication for ${u.email}? They'll set it up again at their next sign-in.`)) return;
    try {
      await api.post(`/users/${u.id}/reset-mfa`);
      toast.success(`MFA reset for ${u.email}`);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
  };

  const doDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message); }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="tinylabel">Access control</div>
          <h1 className="text-3xl font-black">Users</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage who can sign in and their permissions.</p>
        </div>
        <button data-testid="new-user-btn" onClick={openNew}
          className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2">
          <Plus size={18} /> New user
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Linked Worker</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && <tr><td className="px-4 py-6 text-slate-400" colSpan={5}>No users.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium">{u.name}{u.id === me.id && <span className="ml-2 text-xs text-green-600 font-bold">(you)</span>}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider bg-slate-100 text-slate-700">{u.role}</span>
                  {u.mfa_enabled && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800" data-testid={`mfa-badge-${u.id}`}>
                      <ShieldCheck size={11} weight="fill" /> 2FA
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">{u.role === "contractor" ? workerName(u.worker_id) : "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}
                    className="text-green-600 font-semibold text-xs mr-3">Edit</button>
                  <button onClick={() => { setPwOpenFor(u); setNewPw(""); }} data-testid={`reset-pw-${u.id}`}
                    className="text-slate-600 hover:text-slate-900 text-xs font-semibold mr-3 inline-flex items-center gap-1">
                    <Key size={13} /> Reset password
                  </button>
                  {u.mfa_enabled && me.role === "admin" && (
                    <button onClick={() => doResetMfa(u)} data-testid={`reset-mfa-${u.id}`}
                      className="text-amber-700 hover:text-amber-900 text-xs font-semibold mr-3 inline-flex items-center gap-1">
                      <ShieldCheck size={13} /> Reset MFA
                    </button>
                  )}
                  {u.id !== me.id && (
                    <button onClick={() => doDelete(u)} data-testid={`delete-user-${u.id}`}
                      className="text-red-600 hover:text-red-800 text-xs font-semibold inline-flex items-center gap-1">
                      <Trash size={13} /> Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={save} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingId ? "Edit user" : "New user"}</h2>
              <button type="button" onClick={() => setOpen(false)}><X size={20} /></button>
            </div>

            <div className="mb-3">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Full name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="user-input-name" className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md" />
            </div>

            <div className="mb-3">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</label>
              <input required type="email" disabled={!!editingId} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-input-email"
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md disabled:bg-slate-100 disabled:text-slate-500" />
              {editingId && <div className="text-xs text-slate-400 mt-1">Email cannot be changed. Delete and recreate if needed.</div>}
            </div>

            <div className="mb-3">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                data-testid="user-input-role"
                className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md bg-white">
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            {form.role === "contractor" && (
              <div className="mb-3">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Link to Worker</label>
                <WorkerCombobox
                  value={form.worker_id}
                  onChange={(v) => setForm({ ...form, worker_id: v })}
                  workers={availableWorkers}
                  testId="user-input-worker"
                  placeholder="Select a worker…"
                  required
                />
                <div className="text-xs text-slate-400 mt-1">One-to-one link. Contractor will only see their own assignments and timesheets.</div>
              </div>
            )}

            {!editingId && (
              <div className="mb-3">
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Initial password</label>
                <input required type="text" minLength={8} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="user-input-password"
                  className="mt-1 w-full h-11 px-3 border border-slate-300 rounded-md font-mono" />
                <div className="text-xs text-slate-400 mt-1">Min 8 characters. Share this with the user securely — they can change it after login.</div>
              </div>
            )}

            {err && <div className="text-red-600 text-sm">{err}</div>}
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setOpen(false)} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="save-user-btn" className="h-11 px-4 bg-green-600 text-white font-semibold rounded-md">
                {editingId ? "Save changes" : "Create user"}
              </button>
            </div>
          </form>
        </div>
      )}

      {pwOpenFor && (
        <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50">
          <form onSubmit={doReset} className="bg-white rounded-md w-full max-w-md p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xl font-bold">Reset password</h2>
              <button type="button" onClick={() => setPwOpenFor(null)}><X size={20} /></button>
            </div>
            <div className="text-sm text-slate-600 mb-4">Set a new password for <b>{pwOpenFor.email}</b>. Share it with them securely.</div>
            <input autoFocus required minLength={8} type="text" value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              data-testid="reset-pw-input"
              placeholder="Min 8 characters"
              className="w-full h-11 px-3 border border-slate-300 rounded-md font-mono" />
            <div className="mt-6 flex gap-3 justify-end">
              <button type="button" onClick={() => setPwOpenFor(null)} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
              <button data-testid="confirm-reset-pw-btn" className="h-11 px-4 bg-green-600 text-white font-semibold rounded-md">Reset password</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
